'use client'

import posthog, { type CaptureResult } from 'posthog-js'

import { deployEnvironment } from '@/lib/env'

let initialized = false

type PostHogException = {
  type?: unknown
  value?: unknown
  mechanism?: {
    synthetic?: unknown
  }
}

const NOISY_EXCEPTION_VALUES = new Set([
  'CustomEvent captured as exception with keys: isTrusted',
  'Event captured as exception with keys: isTrusted',
])

function getFirstException(event: CaptureResult | null): PostHogException | null {
  if (event?.event !== '$exception') {
    return null
  }

  const exceptionList = event.properties?.$exception_list
  if (!Array.isArray(exceptionList) || exceptionList.length === 0) {
    return null
  }

  return exceptionList[0] as PostHogException
}

function filterNoisyExceptionEvent(event: CaptureResult | null): CaptureResult | null {
  const exception = getFirstException(event)
  if (!exception) {
    return event
  }

  const isNoisySyntheticBrowserEvent =
    exception.mechanism?.synthetic === true &&
    (exception.type === 'CustomEvent' || exception.type === 'Event') &&
    typeof exception.value === 'string' &&
    NOISY_EXCEPTION_VALUES.has(exception.value)

  return isNoisySyntheticBrowserEvent ? null : event
}

export function initPostHogClient() {
  if (initialized) {
    return
  }

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST
  if (!key || !host) {
    return
  }

  posthog.init(key, {
    api_host: '/ingest',
    ui_host: host,
    defaults: '2025-11-30',
    opt_in_site_apps: true,
    capture_exceptions: true,
    before_send: filterNoisyExceptionEvent,
    debug: process.env.NODE_ENV === 'development',
  })

  // Tag every client event (including $exception) so dev/preview/prod are distinguishable,
  // mirroring the server-side `environment`/`host` properties.
  posthog.register({
    environment: deployEnvironment(),
    host: typeof window !== 'undefined' ? window.location.host : null,
  })

  initialized = true
}
